import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { LifeBuoy, Plus } from "lucide-react";
import { api, errMsg, useFetch } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PageHeader, Loading, Empty } from "../components/bits";

const ST = { open: "Envoyée", in_progress: "En cours", resolved: "Résolue", closed: "Clôturée" };

export function TicketForm({ category = "assistance", onDone, testid = "ticket-form" }) {
  const { current } = useApp();
  const [f, setF] = useState({ subject: category === "billing" ? "Je souhaite être contactée par AVELIS" : "", message: "" });
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/support/tickets", { category, ...f, structure_id: current && !current.support ? current.id : null });
      toast.success("Demande envoyée. Nous vous répondons rapidement.");
      onDone?.(data);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={send} className="space-y-3" data-testid={testid}>
      <input className="field" required minLength={3} placeholder="Sujet" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid={`${testid}-subject`} />
      <textarea className="field min-h-[110px]" required minLength={3} placeholder={category === "billing" ? "Vos disponibilités pour être recontactée, vos questions…" : "Décrivez votre question. N'indiquez aucune donnée de patient."} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid={`${testid}-message`} />
      <button className="btn-primary w-full" disabled={busy} data-testid={`${testid}-submit`}>{busy ? "Envoi…" : "Envoyer"}</button>
    </form>
  );
}

export default function Assistance() {
  const { data, reload } = useFetch("/support/tickets");
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  return (
    <div data-testid="assistance-page">
      <PageHeader title="Assistance" subtitle="Posez votre question à l'équipe AVELIS." back="/aide" />
      {open ? <div className="card mb-6 p-5"><TicketForm onDone={(t) => { setOpen(false); reload(); nav(`/assistance/${t.id}`); }} /></div>
        : <button className="btn-primary mb-6 w-full" onClick={() => setOpen(true)} data-testid="new-ticket-btn"><Plus className="h-5 w-5" /> Nouvelle demande</button>}
      {!data ? <Loading /> : data.length === 0 ? <Empty icon={LifeBuoy} text="Aucune demande pour l'instant." /> : (
        <div className="card divide-y divide-slate-100">{data.map((t) => (
          <Link key={t.id} to={`/assistance/${t.id}`} className="flex items-center justify-between gap-2 px-4 py-3" data-testid="my-ticket-row">
            <span className="font-semibold">{t.subject}</span><span className="text-xs font-bold text-teal-700">{ST[t.status]}</span>
          </Link>
        ))}</div>
      )}
    </div>
  );
}

export function AssistanceDetail() {
  const { id } = useParams();
  const { data: t, reload } = useFetch(`/support/tickets/${id}`);
  const [text, setText] = useState("");
  if (!t) return <Loading />;
  const send = async () => { try { await api.post(`/support/tickets/${id}/messages`, { text }); setText(""); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="assistance-detail">
      <PageHeader title={t.subject} subtitle={ST[t.status]} back="/assistance" />
      <div className="space-y-3">{t.messages.map((m) => (
        <div key={m.id} className={`rounded-2xl p-3.5 ${m.from === "avelis" ? "mr-8 bg-teal-50" : "ml-8 bg-white ring-1 ring-slate-200"}`} data-testid="ticket-message">
          <div className="mb-1 text-xs text-slate-500">{m.from === "avelis" ? "Support AVELIS" : "Vous"} · {new Date(m.at).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" })}</div>
          <div className="whitespace-pre-line">{m.text}</div>
        </div>
      ))}</div>
      {t.status !== "closed" && (
        <div className="mt-5 space-y-2">
          <textarea className="field min-h-[80px]" value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre message" data-testid="ticket-user-reply-input" />
          <button className="btn-primary w-full" disabled={!text.trim()} onClick={send} data-testid="ticket-user-reply-btn">Envoyer</button>
        </div>
      )}
    </div>
  );
}
